import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import 'tailwindcss/tailwind.css';
import "../../../../App.css";
import { verifyEmail, reenviarVerificacion } from '../../../http/Actions/AuthAction';

const EmailVerificadorPage = () => {
  const [verificationStatus, setVerificationStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const hasVerified = React.useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;
    // Extraer el token de los parámetros de la URL
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    if (!token) {
      setVerificationStatus('error');
      setErrorMessage('No se encontró el token de verificación');
      navigate('/', { replace: true });
      return;
    }

    // Verificar el email usando el token
    verifyEmail(token, setErrorMessage)
      .then(data => {
        setVerificationStatus('success');
      })
      .catch(error => {
        setVerificationStatus('error');
        // setErrorMessage ya se establece en la función verifyEmail
      });
  }, [location, navigate]);

  // Componente de carga
  const LoadingState = () => (
    <div className="text-center">
      <div className="mx-auto w-16 h-16 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin mb-4"></div>
      <h3 className="text-xl font-bold text-white mb-2">Verificando email...</h3>
      <p className="text-gray-400 mb-6">
        Estamos procesando tu solicitud. Por favor, espera un momento.
      </p>
    </div>
  );

  // Componente de éxito
  const SuccessState = () => (
    <div className="text-center">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      
      <h3 className="text-xl font-bold text-white mb-2">¡Verificación exitosa!</h3>
      <p className="text-gray-400 mb-6">
        Tu correo electrónico ha sido verificado correctamente. Ahora puedes iniciar sesión y disfrutar de todos los beneficios de tu cuenta.
      </p>
      
      <Link to="/login"
        className="inline-block w-full py-3 px-4 border border-transparent rounded-lg
                text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                focus:ring-red-500 font-bold transition duration-300"
      >
        Ir a iniciar sesión
      </Link>
    </div>
  );

  // Componente de error con opción para reenviar verificación
  const ErrorState = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reenvioMessage, setReenvioMessage] = useState('');
    const [formState, setFormState] = useState('initial'); // 'initial', 'form', 'sent'
    
    const handleReenviar = async (e) => {
      e.preventDefault();
      if (!email) {
        setReenvioMessage('Por favor ingresa tu correo electrónico');
        return;
      }
      
      setIsSubmitting(true);
      try {
        await reenviarVerificacion({ Email: email }, (errorMsg) => {
          setReenvioMessage(errorMsg);
        });
        setReenvioMessage('Si la cuenta existe, se ha enviado un nuevo correo de verificación.');
        setFormState('sent'); // Cambiamos a estado "enviado" en lugar de ocultar el formulario
      } catch (error) {
        // El error ya se maneja en la función reenviarVerificacion con setError
      } finally {
        setIsSubmitting(false);
      }
    };

    // Verificar si el mensaje de error contiene "expirado" para mostrar la opción de reenvío
    const isExpired = errorMessage && errorMessage.toLowerCase().includes('expirado');

    return (
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        
        <h3 className="text-xl font-bold text-white mb-2">Error de verificación</h3>
        <p className="text-gray-400 mb-6">
          {errorMessage || 'Ha ocurrido un error al verificar tu correo electrónico. Por favor, intenta nuevamente o contacta a soporte.'}
        </p>
        
        {isExpired && (
          <>
            {formState === 'initial' ? (
              <button
                onClick={() => setFormState('form')}
                className="inline-block w-full py-3 px-4 border border-transparent rounded-lg
                      text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                      focus:ring-blue-500 font-bold transition duration-300 mb-4"
              >
                Reenviar verificación
              </button>
            ) : formState === 'form' ? (
              <form onSubmit={handleReenviar} className="mb-4">
                <div className="mb-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ingresa tu correo electrónico"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-zinc-800 text-white"
                    required
                  />
                </div>
                {reenvioMessage && <p className="text-sm text-gray-300 mb-3">{reenvioMessage}</p>}
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormState('initial')}
                    className="flex-1 py-3 px-4 border border-gray-600 rounded-lg
                          text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 
                          focus:ring-gray-500 font-bold transition duration-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-3 px-4 border border-transparent rounded-lg
                          text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                          focus:ring-blue-500 font-bold transition duration-300"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </form>
            ) : (
              // Estado enviado (sent) - Mensaje mejorado
              <div className="mb-4">
                <div className="p-4 bg-green-900 bg-opacity-30 border border-green-500 rounded-lg mb-4">
                  <div className="flex items-center">
                    <div>
                      <p className="text-sm font-medium text-green-300">¡Correo enviado!</p>
                      <p className="text-xs text-green-200 mt-1">
                        Si la cuenta existe, recibirás en breve un nuevo correo de verificación.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        <Link to="/login"
          className="inline-block w-full py-3 px-4 border border-transparent rounded-lg
                  text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                  focus:ring-red-500 font-bold transition duration-300"
        >
          Ir a iniciar sesión
        </Link>
      </div>
    );
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-black relative"
      style={{
        backgroundImage: 'url(https://i.postimg.cc/rw3XpzPP/wallpaper.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay para asegurar que cubra toda la página */}
      <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-black bg-opacity-75"></div>
      
      <div className="z-10 max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="mt-6 text-4xl font-extrabold text-white">
            Verificación de Email
          </h2>
          <p className="mt-2 text-sm text-gray-300">
            {verificationStatus === 'success' ? 'Tu cuenta ha sido activada correctamente' : 'Verificando tu cuenta'}
          </p>
        </div>
        
        <div className="bg-zinc-900 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            {verificationStatus === 'loading' && <LoadingState />}
            {verificationStatus === 'success' && <SuccessState />}
            {verificationStatus === 'error' && <ErrorState />}
          </div>
          
          <div className="px-8 py-6 bg-zinc-950 text-center">
            <p className="text-gray-300">
              ¿Ya tienes tu cuenta lista? <Link to="/login" className="text-red-400 hover:underline font-bold">Iniciar sesión</Link>
            </p>
            <p className="text-xs text-gray-500 mt-4">
              Gracias por unirte a nuestra comunidad. Si tienes alguna pregunta o necesitas ayuda, no dudes en contactar a nuestro equipo de soporte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificadorPage;